import { identity } from 'lodash';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Select } from '~/eds/src/components/Select/Select';
import { useThrottledState } from '~/eds/src/hooks/useThrottledState';
import { Option } from '~/eds/src/types/Option';
import { UserAction } from '~/eds/src/types/UserAction';
import { api } from '~/redux/api/createApi';
import { DataField } from '~/types/Field';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { sortByStringValue } from '~/utils/array';

import { getFieldTypeLabel, getIconFromFieldType } from '../utils';

type Props = {
  value: Nullable<PilotId>;
  onChange: (field: Nullable<DataField>) => void;
  disabled?: boolean;
  footerAction?: UserAction;
  filterOption?: (option: Option<PilotId>) => boolean;
  isLoading?: boolean;
  mapOption?: (
    option: Option<PilotId, DataField>,
  ) => Option<PilotId, DataField>;
  sortOption?: (
    a: Option<PilotId, DataField>,
    b: Option<PilotId, DataField>,
  ) => number;
};

export const FieldSelect = ({
  disabled,
  filterOption,
  footerAction,
  isLoading: overrideIsLoading,
  mapOption = identity,
  onChange,
  sortOption = sortByStringValue('label'),
  value,
}: Props) => {
  const [throttledSearch, _search, setSearch] = useThrottledState('');
  const intl = useIntl();

  const {
    data: searchableFields,
    error: searchableFieldsError,
    isFetching: isFetchingSearchableFields,
  } = api.endpoints.getFields.useQuery({
    limit: 100,
    search: throttledSearch,
  });

  const {
    data: resolvedFields,
    error: resolvedFieldsError,
    isFetching: isFetchingResolvedFields,
  } = api.endpoints.getFields.useQuery(
    {
      limit: 1,
      ids: value ? [value] : undefined,
    },
    {
      skip: !value,
    },
  );

  const error =
    resolvedFieldsError || searchableFieldsError
      ? intl.formatMessage({
          id: 'FieldSelect.loadError',
          defaultMessage: 'Unable to load fields',
          description: 'Error message when fields cannot be loaded',
        })
      : undefined;
  const isLoading =
    overrideIsLoading || isFetchingSearchableFields || isFetchingResolvedFields;

  const options = useMemo(() => {
    const fields = [
      ...(resolvedFields?.results ?? []),
      ...(searchableFields?.results ?? []),
    ];
    return fields
      .map((field) => ({
        data: field,
        label: field.label,
        leftIcon: getIconFromFieldType(field.type),
        value: field.id,
        description: getFieldTypeLabel(field.type, intl),
      }))
      .map(mapOption)
      .sort(sortOption);
  }, [resolvedFields, searchableFields, mapOption, sortOption]);

  const optionsMap: Record<PilotId, DataField> = useMemo(
    () =>
      Object.fromEntries(options.map((option) => [option.value, option.data!])),
    [options],
  );

  const handleChange = (value: Nullable<PilotId>) => {
    onChange(value === null ? null : optionsMap[value]);
  };

  return (
    <Select
      disabled={disabled}
      error={error}
      filterOption={filterOption}
      footerAction={footerAction}
      isClearable={false}
      isLoading={isLoading}
      isMulti={false}
      name="field-select"
      options={options}
      placeholder={intl.formatMessage({
        id: 'FieldSelect.placeholder',
        defaultMessage: 'Search for a field…',
        description: 'Placeholder text for field search input',
      })}
      value={value}
      onChange={handleChange}
      onSearch={setSearch}
    />
  );
};
